import React from 'react';
import { keyframes } from '@mui/material';

/**
 * iaDesign.js
 * Centralized design system for AI features.
 */

// 1. Single Source of Truth for AI Colors
const AI_GRADIENT_SEQUENCE = [
  "rgba(111, 25, 127, 0.8)", // Primary Purple
  "rgba(140, 76, 150, 0.8)", // Soft Lavender
  "rgba(175, 48, 198, 0.9)", // Bright Magenta
  "rgba(211, 107, 230, 0.8)",  // Deep Neon
  "rgba(156, 39, 176, 0.8)"  // Loop back to start
];

const GRADIENT_STRING = AI_GRADIENT_SEQUENCE.join(', ');

// Animations
const waveFlow = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const auraFlow = keyframes`
  0% { box-shadow: 0 0 5px rgba(156, 39, 176, 0.03); }
  50% { box-shadow: 0 0 10px rgba(213, 0, 249, 0.2); }
  100% { box-shadow: 0 0 5px rgba(156, 39, 176, 0.05); }
`;

const shimmerSlide = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const aiPulse = keyframes`
  0% { transform: scale(1); opacity: 1; filter: drop-shadow(0 0 0 rgba(156, 39, 176, 0)); }
  50% { transform: scale(1.3); opacity: 0.8; filter: drop-shadow(0 0 12px rgba(156, 39, 176, 0.6)); }
  100% { transform: scale(1); opacity: 1; filter: drop-shadow(0 0 0 rgba(156, 39, 176, 0)); }
`;

const TOKENS = {
  colors: {
    primary: '#9c27b0',
    // The animated border uses a lighter/more transparent version for subtlety
    borderFlowGradient: `linear-gradient(90deg, 
      rgba(156, 39, 176, 0.15), rgba(224, 62, 253, 0.4), 
      rgba(213, 0, 249, 0.2), rgba(156, 39, 176, 0.15))`,
    // Shared text/icon gradient
    sharedGradient: `linear-gradient(90deg, ${GRADIENT_STRING})`,
    disabled: '#999',
    disabledBg: '#f5f5f5',
    disabledBorder: '#ccc',
    cardBgGradient: 'linear-gradient(140deg, #ffffff 40%, rgb(250, 248, 250) 100%)'
  },
  border: { radius: '5px', width: '2px' },
  animation: { duration: '4s', timing: 'linear', iteration: 'infinite' }
};

/**
 * Component: SVG Gradient using the unified token sequence.
 */
export const IconGradientDefinition = () => {
  const animatedValues = AI_GRADIENT_SEQUENCE.join('; ');

  return (
    <svg width={0} height={0} style={{ position: 'absolute', visibility: 'hidden' }}>
      <defs>
        <linearGradient id="icon-gradient" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%">
            <animate
              attributeName="stop-color"
              values={animatedValues}
              dur={TOKENS.animation.duration}
              repeatCount="indefinite"
            />
          </stop>
          <stop offset="100%">
            <animate
              attributeName="stop-color"
              // Offset values for 100% to create a dynamic look
              values={[...AI_GRADIENT_SEQUENCE].reverse().join('; ')}
              dur={TOKENS.animation.duration}
              repeatCount="indefinite"
            />
          </stop>
        </linearGradient>
      </defs>
    </svg>
  );
};

export const getIAStyles = (type = 'card', isLoading = false) => {
  const borderGradientStyles = {
    content: '""',
    position: 'absolute',
    inset: `-${TOKENS.border.width}`,
    padding: TOKENS.border.width,
    borderRadius: TOKENS.border.radius,
    background: TOKENS.colors.borderFlowGradient,
    backgroundSize: '200% 100%',
    animation: `${waveFlow} ${TOKENS.animation.duration} ${TOKENS.animation.timing} ${TOKENS.animation.iteration}`,
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    pointerEvents: 'none',
    zIndex: 0
  };

  const textGradientStyles = {
    background: TOKENS.colors.sharedGradient,
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: `${shimmerSlide} 8s linear infinite`,
  };

  switch (type) {
    case 'button':
      return {
        position: 'relative',
        backgroundColor: 'white',
        border: 'none',
        padding: '3px 9px',
        color: TOKENS.colors.primary,
        borderRadius: TOKENS.border.radius,
        fontWeight: 400,
        animation: `${auraFlow} ${TOKENS.animation.duration} ${TOKENS.animation.timing} ${TOKENS.animation.iteration}`,
        '&::before': borderGradientStyles,
        '& .MuiButton-startIcon svg': {
          fill: 'url(#icon-gradient) !important',
          filter: 'drop-shadow(0px 1px 2px rgba(156, 39, 176, 0.1))'
        },
        '&:hover': { backgroundColor: 'white' },
        '&:disabled': isLoading ? {
          cursor: 'wait',
          pointerEvents: 'auto',
          color: TOKENS.colors.primary,
          opacity: 1,
          backgroundColor: 'white !important',
          '& .MuiButton-startIcon svg': {
            animation: `${aiPulse} 1.5s ease-in-out infinite !important`
          }
        } : {
          backgroundColor: TOKENS.colors.disabledBg,
          color: TOKENS.colors.disabled,
          '&::before': { background: TOKENS.colors.disabledBorder, animation: 'none' },

          '& .MuiButton-startIcon svg': {
            fill: `${TOKENS.colors.disabled} !important`,
            filter: 'none !important',
            opacity: 0.6
          }
        }

      };

    case 'card':
      return {
        position: 'relative',
        background: 'transparent',
        boxShadow: 'none',
        overflow: 'visible',
        '&::before': { ...borderGradientStyles, borderRadius: '6px' }
      };

    case 'cardContent':
      return {
        position: 'relative',
        zIndex: 1,
        borderRadius: TOKENS.border.radius,
        background: TOKENS.colors.cardBgGradient,
        animation: `${auraFlow} ${TOKENS.animation.duration} ${TOKENS.animation.timing} ${TOKENS.animation.iteration}`
      };

    case 'snack':
      return {
        position: 'relative',
        overflow: 'visible',
        borderRadius: '10px',
        color: TOKENS.colors.primary,
        '&::before': { ...borderGradientStyles, inset: '-3px', padding: '3px', borderRadius: '10px' },
        '& .MuiAlert-icon': {
          color: `${TOKENS.colors.primary} !important`,
          '& svg': {
            fill: 'url(#icon-gradient) !important',
          }
        },

      };

    case 'textButton':
      return {
        animation: `${auraFlow} ${TOKENS.animation.duration} ${TOKENS.animation.timing} ${TOKENS.animation.iteration}`,
        '& .MuiButton-startIcon svg': { fill: 'url(#icon-gradient) !important' },
        '& .ai-text-gradient': textGradientStyles
      };

    case 'iconOnly':
      return {
        ...(isLoading && {
          cursor: 'wait',
          pointerEvents: 'auto',
          '&.Mui-disabled': {
            cursor: 'wait',
            pointerEvents: 'auto'
          }
        }),
        '& svg': {
          fill: 'url(#icon-gradient) !important',
        },
        '&:hover svg': { filter: 'drop-shadow(0px 1px 5px rgba(213, 0, 249, 0.6))' },
        '&.Mui-disabled svg': {
          fill: `${TOKENS.colors.disabled} !important`,
          filter: 'none !important',
          opacity: 0.6
        }
      };

    case 'suggestionItem':
      return {
        '& .ai-suggestion-text': {
          color: 'rgba(123, 31, 162, 1)',
        }
      };

    case 'iconOnlyLoading':
      return {
        pointerEvents: 'auto',
        cursor: 'wait',
        '&.Mui-disabled': {
          opacity: 1,
          cursor: 'wait',
          pointerEvents: 'auto'
        },
        '& svg': {
          fill: 'url(#icon-gradient) !important',
          animation: `${aiPulse} 1.5s ease-in-out infinite`,
        },
        '&.Mui-disabled svg': {
          fill: 'url(#icon-gradient) !important',
          opacity: 1,
        }
      }

    default:
      return {};


  }
};